import * as A from '../clients/mira/types/Application';
import * as P from '../clients/mira/types/Presentation';
import * as T from '../clients/mira/types/Theme';
import createDefaultResource from './createDefaultResource';

export default function createPresentationFromAppVersion(
  appVersion: A.ApplicationVersion,
  profileId: string,
  theme?: T.Theme,
  domainDefaultDuration?: number | null,
) {
  // collect default application vars for presentation
  const defaultAppVars =
    appVersion.presentationProperties.reduce<P.ApplicationVariables>(
      (vars, prop) => ({
        ...vars,
        [prop.name]: prop.default,
      }),
      {},
    );

  // Seed the duration field from the domain-level default when one is set, so a
  // new (not-dirty) presentation starts with the domain default instead of the
  // app version or presentation property default. Users can still override it.
  // Apps model duration two ways: the built-in duration (configurableDuration)
  // and/or a presentation property named "duration" or "presentationDuration"
  // (an application variable).
  if (domainDefaultDuration != null) {
    const durationProperty = appVersion.presentationProperties.find(
      (prop) =>
        prop.name === 'duration' || prop.name === 'presentationDuration',
    );
    if (durationProperty) {
      defaultAppVars[durationProperty.name] = domainDefaultDuration;
    }
  }

  const presentation: P.Presentation = {
    // id and userId will be set on save in the api.
    id: '',
    name: appVersion.name,
    applicationVariables: defaultAppVars,
    applicationId: appVersion.applicationId,
    appVersionId: appVersion.id,
    applicationName: appVersion.name,
    presentationProperties: appVersion.presentationProperties,
    thumbnailUrl: appVersion.thumbnailUrl,
    applicationThumbnailUrl: appVersion.thumbnailUrl,
    iconUrl: appVersion.iconUrl,
    hasDynamicThumbnails: appVersion.dynamicThumbnails,
    sourceUrl: appVersion.sourceUrl,
    fileUploads: null,
    resource: createDefaultResource({ profile: { id: profileId } }),
  };

  if (theme) {
    presentation.themeId = theme.id;
  }

  if (appVersion.configurableDuration) {
    presentation.duration =
      domainDefaultDuration != null
        ? domainDefaultDuration
        : appVersion.defaultDuration;
  }

  return presentation;
}
